import React, { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Modal = ({ isOpen, onClose, painting, onNext, onPrev }) => {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language?.startsWith('en');

  // Handle escape key
  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowRight') onNext?.();
    if (e.key === 'ArrowLeft') onPrev?.();
  }, [onClose, onNext, onPrev]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleEscape]);

  if (!isOpen || !painting) return null;

  const getStatusBadge = (status) => {
    const badges = {
      available: { class: 'badge-available', text: t('modal.status.available') },
      sold: { class: 'badge-sold', text: t('modal.status.sold') },
      private: { class: 'badge-private', text: t('modal.status.private') },
    };
    return badges[status] || badges.available;
  };

  const status = getStatusBadge(painting.status);
  const displayTitle = isEnglish ? (painting.titleEn || painting.title) : painting.title;
  const altTitle = isEnglish ? painting.title : painting.titleEn;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center safe-area-inset-x safe-area-inset-y">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#2A2520]/90 backdrop-blur-sm animate-[fadeIn_0.3s_ease]"
        onClick={onClose}
      />

      {/* Navigation Arrows - hidden on very small screens, touch-friendly size */}
      {onPrev && (
        <button
          onClick={onPrev}
          className="hidden xs:flex absolute left-2 xs:left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 w-11 h-11 xs:w-12 xs:h-12 rounded-full bg-white/10 backdrop-blur-sm items-center justify-center text-white hover:bg-white/20 transition-all duration-300"
          aria-label={t('modal.previousPainting')}
        >
          <ChevronLeft size={24} />
        </button>
      )}

      {onNext && (
        <button
          onClick={onNext}
          className="hidden xs:flex absolute right-2 xs:right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 w-11 h-11 xs:w-12 xs:h-12 rounded-full bg-white/10 backdrop-blur-sm items-center justify-center text-white hover:bg-white/20 transition-all duration-300"
          aria-label={t('modal.nextPainting')}
        >
          <ChevronRight size={24} />
        </button>
      )}

      {/* Modal Content */}
      <div
        className="relative w-full max-w-6xl max-h-[90vh] mx-2 xs:mx-4 bg-[#FAF7F2] overflow-hidden animate-[scaleIn_0.4s_ease]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button - touch-friendly 44x44px */}
        <button
          onClick={onClose}
          className="absolute top-2 xs:top-4 right-2 xs:right-4 z-10 w-11 h-11 rounded-full bg-[#FAF7F2]/90 backdrop-blur-sm flex items-center justify-center text-[#3D3831] hover:bg-[#E8E2D9] transition-colors duration-300"
          aria-label={t('modal.close')}
        >
          <X size={20} />
        </button>

        <div className="flex flex-col lg:flex-row max-h-[90vh] overflow-auto">
          {/* Image */}
          <div className="lg:w-2/3 bg-[#E8E2D9] flex items-center justify-center p-2 xs:p-4 lg:p-8">
            <img
              src={painting.url}
              alt={displayTitle}
              className="max-w-full max-h-[40vh] xs:max-h-[50vh] lg:max-h-[80vh] object-contain shadow-2xl"
            />
          </div>

          {/* Details */}
          <div className="lg:w-1/3 p-4 xs:p-6 lg:p-10 flex flex-col">
            <div className="mb-6">
              <span className={status.class}>{status.text}</span>
            </div>

            <h2 className="text-2xl xs:text-3xl lg:text-4xl mb-2 text-[#3D3831]">
              {displayTitle}
            </h2>

            {altTitle && (
              <p className="text-sm text-[#8B7E6A] italic mb-6">
                {altTitle}
              </p>
            )}

            <div className="divider-elegant mb-6 mx-0" />

            <div className="space-y-3 mb-8">
              <div className="flex justify-between">
                <span className="text-[#8B7E6A] text-sm">{t('modal.yearCreated')}</span>
                <span className="text-[#3D3831]">{painting.year}</span>
              </div>
              {painting.size && (
                <div className="flex justify-between">
                  <span className="text-[#8B7E6A] text-sm">{t('modal.size')}</span>
                  <span className="text-[#3D3831]">{painting.size}</span>
                </div>
              )}
              {painting.technique && (
                <div className="flex justify-between">
                  <span className="text-[#8B7E6A] text-sm">{t('modal.technique')}</span>
                  <span className="text-[#3D3831]">{painting.technique}</span>
                </div>
              )}
              {painting.price && painting.status === 'available' && (
                <div className="flex justify-between">
                  <span className="text-[#8B7E6A] text-sm">{t('modal.price')}</span>
                  <span className="text-[#B8963E] font-medium">
                    {painting.price} ₽
                  </span>
                </div>
              )}
            </div>

            <p className="text-[#5A5249] leading-relaxed mb-8 flex-grow">
              {painting.description}
            </p>

            <div className="space-y-3 mt-auto">
              {painting.status === 'available' && (
                <button className="btn-gold w-full">
                  {t('modal.purchase')}
                </button>
              )}
              <button className="btn-outline w-full">
                {t('modal.orderReproduction')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Keyboard hint - hidden on touch devices */}
      <div className="hidden md:flex absolute bottom-4 left-1/2 -translate-x-1/2 items-center space-x-4 text-white/50 text-xs safe-area-inset-bottom">
        <span>← → {t('modal.navigation')}</span>
        <span>ESC {t('modal.closeHint')}</span>
      </div>
    </div>
  );
};

export default Modal;
